import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ListTodo, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useTasks, useCreateTask, useUpdateTask, WeddingTask } from '@/hooks/useTasks';

const TasksWidget = () => {
  const { data: tasks = [], isLoading } = useTasks();
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const [newTitle, setNewTitle] = useState('');

  const handleToggle = (task: WeddingTask) => {
    updateTask.mutate({ id: task.id, data: { completed: !task.completed } });
  };

  const handleCreate = () => {
    const title = newTitle.trim();
    if (!title) return;
    createTask.mutate(
      { title },
      {
        onSuccess: () => setNewTitle(''),
      },
    );
  };

  const pendingTasks = tasks.filter((t) => !t.completed);
  const visibleTasks = pendingTasks.slice(0, 3);

  return (
    <Card className="shadow-card border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <ListTodo className="w-4 h-4 text-primary" />
          </div>
          Tareas Pendientes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
          className="flex items-center gap-2"
        >
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Nueva tarea"
            className="h-9"
            disabled={createTask.isPending}
          />
          <Button
            type="submit"
            size="sm"
            className="shrink-0"
            disabled={createTask.isPending || !newTitle.trim()}
          >
            <Plus className="w-4 h-4 mr-1" />
            Nueva
          </Button>
        </form>

        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            Cargando tareas...
          </div>
        ) : visibleTasks.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">¡No hay tareas pendientes!</p>
          </div>
        ) : (
          visibleTasks.map((task) => (
            <div
              key={task.id}
              className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id={task.id}
                checked={task.completed}
                onCheckedChange={() => handleToggle(task)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={task.id}
                  className={`text-sm font-medium cursor-pointer ${
                    task.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  {task.title}
                </label>
                {task.due_date && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Vence: {task.due_date}
                  </p>
                )}
              </div>
            </div>
          ))
        )}

        {pendingTasks.length > 3 && (
          <Button variant="link" className="w-full text-primary text-sm">
            Ver todas las tareas ({pendingTasks.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TasksWidget;
